import LeadIntelligencePanel from "../LeadIntelligencePanel";
import ContactModal from "../ContactModal";
import React, { useEffect, useMemo, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  TextField,
  Avatar,
  IconButton,
  Slider,
  Chip
} from "@material-ui/core";
import {
  Close,
  AttachMoney,
  CheckCircle,
  Cancel,
  AccessTime,
  Phone,
  Email,
  Business,
  Public,
  Person,
  Event,
  ChatBubbleOutline,
  Timeline
} from "@material-ui/icons";
import { toast } from "react-toastify";

import {
  createOpportunity,
  updateOpportunity
} from "../../services/opportunities";

const useStyles = makeStyles((theme) => ({
  dialog: {
    "& .MuiDialog-paper": {
      borderRadius: 22,
      overflow: "hidden",
      boxShadow: "0 32px 90px rgba(15,23,42,.28)",
      maxWidth: 720,
      width: "calc(100% - 24px)",
      maxHeight: "92vh"
    }
  },

  header: {
    background:
      "linear-gradient(135deg,#0033CC 0%,#0066FF 55%,#00A3FF 100%)",
    padding: theme.spacing(2.4, 3),
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between"
  },

  headerLeft: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    minWidth: 0
  },

  headerAvatar: {
    width: 46,
    height: 46,
    background: "rgba(255,255,255,.18)",
    border: "2px solid rgba(255,255,255,.28)",
    fontWeight: 900
  },

  headerTitle: {
    color: "#fff",
    fontWeight: 900,
    fontSize: "1rem",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis"
  },

  headerSubtitle: {
    color: "rgba(255,255,255,.72)",
    fontSize: ".73rem",
    marginTop: 2
  },

  closeBtn: {
    color: "#fff",
    flexShrink: 0
  },

  content: {
    padding: theme.spacing(2.5),
    background: theme.palette.type === "dark" ? "#0F172A" : "#F5F7FB",
    overflowY: "auto",
    WebkitOverflowScrolling: "touch"
  },

  leadHero: {
    borderRadius: 18,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    background: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: "0 10px 24px rgba(15,23,42,.05)"
  },

  leadHeader: {
    display: "flex",
    gap: theme.spacing(1.5),
    alignItems: "center"
  },

  leadAvatar: {
    width: 52,
    height: 52,
    borderRadius: 16,
    background: "linear-gradient(135deg,#0066FF,#00A3FF)",
    color: "#fff",
    fontWeight: 900
  },

  leadName: {
    fontWeight: 900,
    fontSize: "1.05rem",
    color: theme.palette.text.primary
  },

  leadCompany: {
    fontSize: ".78rem",
    color: theme.palette.text.secondary,
    marginTop: 2
  },

  chips: {
    display: "flex",
    flexWrap: "wrap",
    gap: 7,
    marginTop: theme.spacing(1.5)
  },

  infoGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: theme.spacing(1.2),
    marginTop: theme.spacing(1.7),
    [theme.breakpoints.down("xs")]: {
      gridTemplateColumns: "1fr"
    }
  },

  infoItem: {
    display: "flex",
    gap: 9,
    alignItems: "flex-start",
    padding: "10px 12px",
    borderRadius: 12,
    background:
      theme.palette.type === "dark"
        ? "rgba(255,255,255,.04)"
        : "#F8FAFC",
    border: `1px solid ${theme.palette.divider}`,
    minWidth: 0
  },

  infoIcon: {
    color: "#0066FF",
    fontSize: 18,
    marginTop: 1,
    flexShrink: 0
  },

  infoLabel: {
    fontSize: ".65rem",
    fontWeight: 800,
    textTransform: "uppercase",
    color: theme.palette.text.secondary,
    letterSpacing: ".4px"
  },

  infoValue: {
    fontSize: ".78rem",
    fontWeight: 700,
    color: theme.palette.text.primary,
    marginTop: 2,
    wordBreak: "break-word"
  },

  section: {
    borderRadius: 18,
    border: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    background: theme.palette.background.paper,
    boxShadow: "0 8px 20px rgba(15,23,42,.04)"
  },

  sectionTitle: {
    fontWeight: 900,
    fontSize: ".75rem",
    color: theme.palette.text.secondary,
    textTransform: "uppercase",
    letterSpacing: ".65px",
    marginBottom: theme.spacing(1.5),
    display: "flex",
    alignItems: "center",
    gap: 7
  },

  field: {
    marginBottom: 14,
    "& .MuiOutlinedInput-root": {
      borderRadius: 12
    },
    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "#0066FF",
      borderWidth: 2
    }
  },

  statusGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3,1fr)",
    gap: 8,
    [theme.breakpoints.down("xs")]: {
      gridTemplateColumns: "1fr"
    }
  },

  statusBtn: {
    borderRadius: 11,
    fontWeight: 800,
    fontSize: ".75rem",
    padding: "9px 6px",
    cursor: "pointer",
    transition: "all .2s",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 6
  },

  probabilityLabel: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center"
  },

  conversationBox: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    padding: "12px 14px",
    borderRadius: 12,
    background: "rgba(0,102,255,.06)",
    border: "1px solid rgba(0,102,255,.14)"
  },

  timelineItem: {
    display: "grid",
    gridTemplateColumns: "14px 1fr",
    gap: 10,
    position: "relative",
    paddingBottom: 18
  },

  timelineDot: {
    width: 11,
    height: 11,
    borderRadius: "50%",
    background: "#0066FF",
    marginTop: 4,
    boxShadow: "0 0 0 4px rgba(0,102,255,.12)"
  },

  timelineTitle: {
    fontSize: ".8rem",
    fontWeight: 800,
    color: theme.palette.text.primary
  },

  timelineDescription: {
    fontSize: ".73rem",
    color: theme.palette.text.secondary,
    marginTop: 3,
    lineHeight: 1.5
  },

  timelineDate: {
    fontSize: ".66rem",
    color: "#94A3B8",
    marginTop: 5
  },

  timelineBadge: {
    display: "inline-flex",
    alignItems: "center",
    borderRadius: 999,
    padding: "3px 8px",
    fontSize: ".62rem",
    fontWeight: 900,
    marginBottom: 7
  },

  timelineStageChange: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 6,
    marginTop: 9
  },

  timelineStageChip: {
    borderRadius: 999,
    padding: "4px 8px",
    fontSize: ".66rem",
    fontWeight: 800,
    background:
      theme.palette.type === "dark"
        ? "rgba(255,255,255,.06)"
        : "#F1F5F9",
    color: theme.palette.text.primary,
    border: `1px solid ${theme.palette.divider}`
  },

  timelineArrow: {
    fontSize: ".7rem",
    fontWeight: 900,
    color: "#94A3B8"
  },

  timelineMessageBox: {
    marginTop: 9,
    padding: "9px 11px",
    borderRadius: 10,
    background:
      theme.palette.type === "dark"
        ? "rgba(37,99,235,.08)"
        : "rgba(37,99,235,.05)",
    border: "1px solid rgba(37,99,235,.14)"
  },

  timelineMessageLabel: {
    fontSize: ".62rem",
    fontWeight: 900,
    color: "#2563EB",
    textTransform: "uppercase",
    letterSpacing: ".4px"
  },

  timelineMessageText: {
    fontSize: ".72rem",
    color: theme.palette.text.primary,
    marginTop: 4,
    lineHeight: 1.45,
    fontStyle: "italic"
  },

  actions: {
    padding: theme.spacing(1.8, 3),
    background: theme.palette.background.paper,
    borderTop: `1px solid ${theme.palette.divider}`,
    gap: theme.spacing(1)
  },

  cancelBtn: {
    borderRadius: 11,
    fontWeight: 700,
    textTransform: "none"
  },

  saveBtn: {
    borderRadius: 11,
    fontWeight: 800,
    textTransform: "none",
    background: "linear-gradient(135deg,#0066FF,#00A3FF)",
    color: "#fff",
    padding: theme.spacing(1, 3),
    boxShadow: "0 7px 18px rgba(0,102,255,.28)",
    "&:hover": {
      background: "linear-gradient(135deg,#0052CC,#0088DD)"
    }
  }
}));

const statusOptions = [
  {
    value: "open",
    label: "Em andamento",
    icon: <AccessTime style={{ fontSize: 15 }} />,
    color: "#B77900",
    bg: "rgba(245,158,11,.11)",
    border: "rgba(245,158,11,.32)"
  },
  {
    value: "won",
    label: "Ganho",
    icon: <CheckCircle style={{ fontSize: 15 }} />,
    color: "#16A34A",
    bg: "rgba(22,163,74,.10)",
    border: "rgba(22,163,74,.28)"
  },
  {
    value: "lost",
    label: "Perdido",
    icon: <Cancel style={{ fontSize: 15 }} />,
    color: "#EF4444",
    bg: "rgba(239,68,68,.10)",
    border: "rgba(239,68,68,.28)"
  }
];

const formatDateTime = value => {
  if (!value) return "Não informado";

  return new Date(value).toLocaleString("pt-BR", {
    dateStyle: "short",
    timeStyle: "short"
  });
};

const getEventVisual = event => {
  const actorType = String(event?.actorType || "").toLowerCase();
  const type = String(event?.type || "").toLowerCase();

  if (actorType === "ai" || type.includes("ai")) {
    return {
      label: "Korax AI",
      color: "#2563EB",
      background: "rgba(37,99,235,.10)"
    };
  }

  if (
    actorType === "whatsapp" ||
    type.includes("whatsapp") ||
    event?.metadata?.source === "WhatsApp"
  ) {
    return {
      label: "WhatsApp",
      color: "#16A34A",
      background: "rgba(22,163,74,.10)"
    };
  }

  if (actorType === "user") {
    return {
      label: "Usuário",
      color: "#7C3AED",
      background: "rgba(124,58,237,.10)"
    };
  }

  return {
    label: "Sistema",
    color: "#64748B",
    background: "rgba(100,116,139,.10)"
  };
};

const getEventStageNames = event => {
  const metadata = event?.metadata || {};

  return {
    previous:
      metadata.previousStage ||
      metadata.fromStageName ||
      null,
    next:
      metadata.newStage ||
      metadata.toStageName ||
      null
  };
};

const getEventMessage = event => {
  const metadata = event?.metadata || {};

  return (
    metadata.leadMessage ||
    metadata.message ||
    metadata.body ||
    null
  );
};

const OpportunityModal = ({
  open,
  onClose,
  ticketId,
  contact,
  opportunity,
  onSaved
}) => {
  const classes = useStyles();
  const [loading, setLoading] = useState(false);

  const [form, setForm] = useState({
    title: "",
    product: "",
    value: "",
    probability: 50,
    status: "open",
    notes: "",
    expectedCloseDate: ""
  });

  const resolvedContact = useMemo(
    () => opportunity?.contact || contact || opportunity?.ticket?.contact || null,
    [opportunity, contact]
  );

  const resolvedTicketId = opportunity?.ticketId || ticketId || null;
  const stage = opportunity?.stage || null;
  const events = opportunity?.events || [];

  const companyName = useMemo(() => {
    if (opportunity?.title?.includes(" — ")) {
      return opportunity.title.split(" — ").slice(1).join(" — ");
    }

    const eventCompany = events.find(
      event => event?.metadata?.companyName
    );

    return eventCompany?.metadata?.companyName || "Empresa não informada";
  }, [opportunity, events]);

  useEffect(() => {
    if (opportunity) {
      setForm({
        title: opportunity.title || "",
        product: opportunity.product || "",
        value: opportunity.value ?? "",
        probability: Number(opportunity.probability ?? 50),
        status: opportunity.status || "open",
        notes: opportunity.notes || "",
        expectedCloseDate: opportunity.expectedCloseDate
          ? opportunity.expectedCloseDate.split("T")[0]
          : ""
      });
    } else {
      setForm({
        title: "",
        product: "",
        value: "",
        probability: 50,
        status: "open",
        notes: "",
        expectedCloseDate: ""
      });
    }
  }, [opportunity, open]);

  const handleSave = async () => {
    setLoading(true);

    try {
      const autoTitle =
        form.title ||
        resolvedContact?.name ||
        "Nova Oportunidade";

      const payload = {
        ...form,
        title: autoTitle,
        value: Number(form.value || 0),
        ticketId: resolvedTicketId,
        contactId: resolvedContact?.id || null
      };

      if (opportunity) {
        await updateOpportunity(opportunity.id, payload);
        toast.success("Oportunidade atualizada!");
      } else {
        await createOpportunity(payload);
        toast.success("Oportunidade criada!");
      }

      if (onSaved) {
        await onSaved();
      }

      onClose();
    } catch (error) {
      console.error(error);
      toast.error("Erro ao salvar oportunidade");
    } finally {
      setLoading(false);
    }
  };

  // KORAX 17AU - edicao segura do cadastro comercial.
  const [contactEditOpen, setContactEditOpen] = useState(false);
  const [contactEdited, setContactEdited] = useState(null);

  const editableContactId =
    resolvedContact?.id || opportunity?.contactId;

  useEffect(() => {
    setContactEdited(null);
    setContactEditOpen(false);
  }, [open, editableContactId]);

  const displayContact =
    contactEdited &&
    String(contactEdited.id) === String(editableContactId)
      ? { ...resolvedContact, ...contactEdited }
      : resolvedContact;

  const initials = displayContact?.name
    ? displayContact.name
        .trim()
        .split(/\s+/)
        .slice(0, 2)
        .map(part => part[0])
        .join("")
        .toUpperCase()
    : "OP";

  return (
    <Dialog PaperProps={{ className: "korax-lead-v3-dialog" }}
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      className={classes.dialog}
    >
      <div className={classes.header}>
        <div className={classes.headerLeft}>
          <Avatar className={classes.headerAvatar}>
            {initials}
          </Avatar>

          <div style={{ minWidth: 0 }}>
            <Typography className={classes.headerTitle}>
              {opportunity
                ? displayContact?.name || "Detalhes da oportunidade"
                : "Nova oportunidade"}
            </Typography>

            <Typography className={classes.headerSubtitle}>
              Ficha comercial completa do lead
            </Typography>
          </div>
        </div>

        <IconButton
          size="small"
          onClick={onClose}
          className={classes.closeBtn}
        >
          <Close fontSize="small" />
        </IconButton>
      </div>

      <ContactModal
        open={contactEditOpen}
        contactId={editableContactId}
        lockNumber
        onClose={() => setContactEditOpen(false)}
        onSave={updated => {
          const nextContact = updated?.contact || updated;

          if (nextContact && nextContact.id) {
            setContactEdited(nextContact);
          }
        }}
      />

      <DialogContent className={classes.content} style={{ background: "#F7F9FD" }}>
        {opportunity && (
          <div className={classes.leadHero}>
            <div className={classes.leadHeader}>
              <Avatar className={classes.leadAvatar}>
                {initials}
              </Avatar>

              <div style={{ minWidth: 0 }}>
                <Typography className={classes.leadName}>
                  {displayContact?.name || opportunity.title}
                </Typography>

                <Typography className={classes.leadCompany}>
                  {companyName}
                </Typography>
              </div>

              <Button
                size="small"
                variant="outlined"
                onClick={() => setContactEditOpen(true)}
                disabled={!editableContactId}
                style={{
                  marginLeft: "auto",
                  borderRadius: 8,
                  textTransform: "none",
                  fontWeight: 800,
                  whiteSpace: "nowrap",
                  color: "#0057FF",
                  borderColor: "#0057FF"
                }}
              >
                Editar contato
              </Button>
            </div>

            <div className={classes.chips}>
              <Chip
                size="small"
                label={stage?.name || "Sem etapa"}
                style={{
                  fontWeight: 800,
                  background: stage?.color
                    ? `${stage.color}18`
                    : "rgba(0,102,255,.08)",
                  color: stage?.color || "#0066FF",
                  border: `1px solid ${stage?.color || "#0066FF"}44`
                }}
              />

              <Chip
                size="small"
                label={opportunity.source || "Manual"}
                style={{
                  fontWeight: 800,
                  background: "#F1F5F9",
                  color: "#475569"
                }}
              />

              <Chip
                size="small"
                label={
                  opportunity.status === "won"
                    ? "Ganho"
                    : opportunity.status === "lost"
                    ? "Perdido"
                    : "Em andamento"
                }
                style={{
                  fontWeight: 800,
                  background: "#F1F5F9",
                  color: "#475569"
                }}
              />
            </div>

            <div className={classes.infoGrid}>
              <div className={classes.infoItem}>
                <Phone className={classes.infoIcon} />
                <div>
                  <Typography className={classes.infoLabel}>
                    Telefone
                  </Typography>
                  <Typography className={classes.infoValue}>
                    {displayContact?.number || "Não informado"}
                  </Typography>
                </div>
              </div>

              <div className={classes.infoItem}>
                <Email className={classes.infoIcon} />
                <div>
                  <Typography className={classes.infoLabel}>
                    E-mail
                  </Typography>
                  <Typography className={classes.infoValue}>
                    {displayContact?.email || "Não informado"}
                  </Typography>
                </div>
              </div>

              <div className={classes.infoItem}>
                <Business className={classes.infoIcon} />
                <div>
                  <Typography className={classes.infoLabel}>
                    Empresa
                  </Typography>
                  <Typography className={classes.infoValue}>
                    {companyName}
                  </Typography>
                </div>
              </div>

              <div className={classes.infoItem}>
                <Public className={classes.infoIcon} />
                <div>
                  <Typography className={classes.infoLabel}>
                    Origem
                  </Typography>
                  <Typography className={classes.infoValue}>
                    {opportunity.source || "Manual"}
                  </Typography>
                </div>
              </div>

              <div className={classes.infoItem}>
                <Person className={classes.infoIcon} />
                <div>
                  <Typography className={classes.infoLabel}>
                    Responsável
                  </Typography>
                  <Typography className={classes.infoValue}>
                    {opportunity.user?.name || "Não atribuído"}
                  </Typography>
                </div>
              </div>

              <div className={classes.infoItem}>
                <Event className={classes.infoIcon} />
                <div>
                  <Typography className={classes.infoLabel}>
                    Data de entrada
                  </Typography>
                  <Typography className={classes.infoValue}>
                    {formatDateTime(opportunity.createdAt)}
                  </Typography>
                </div>
              </div>
            </div>
          </div>
        )}

        {opportunity && (
          <LeadIntelligencePanel
            opportunity={opportunity}
            contact={displayContact}
            ticketId={resolvedTicketId}
          />
        )}

        {opportunity && (
          <div className={classes.section}>
            <Typography className={classes.sectionTitle}>
              <ChatBubbleOutline style={{ fontSize: 17 }} />
              Conversa
            </Typography>

            <div className={classes.conversationBox}>
              <div>
                <Typography
                  style={{
                    fontWeight: 800,
                    fontSize: ".8rem",
                    color: resolvedTicketId ? "#16A34A" : "#D97706"
                  }}
                >
                  {resolvedTicketId
                    ? "Conversa vinculada"
                    : "Conversa ainda não iniciada"}
                </Typography>

                <Typography
                  style={{
                    fontSize: ".72rem",
                    color: "#64748B",
                    marginTop: 3
                  }}
                >
                  {resolvedTicketId
                    ? `Ticket #${resolvedTicketId}`
                    : "O lead existe no CRM, mas ainda não iniciou atendimento pelo WhatsApp."}
                </Typography>
              </div>
            </div>
          </div>
        )}

        {opportunity && (
          <div className={classes.section}>
            <Typography className={classes.sectionTitle}>
              <Timeline style={{ fontSize: 17 }} />
              Histórico
            </Typography>

            {events.length > 0 ? (
              events.map(event => {
                const visual = getEventVisual(event);
                const stageNames = getEventStageNames(event);
                const eventMessage = getEventMessage(event);

                return (
                  <div
                    key={event.id}
                    className={classes.timelineItem}
                  >
                    <div
                      className={classes.timelineDot}
                      style={{
                        background: visual.color,
                        boxShadow: `0 0 0 4px ${visual.background}`
                      }}
                    />

                    <div>
                      <div
                        className={classes.timelineBadge}
                        style={{
                          color: visual.color,
                          background: visual.background
                        }}
                      >
                        {visual.label}
                      </div>

                      <Typography className={classes.timelineTitle}>
                        {event.title}
                      </Typography>

                      {event.description && (
                        <Typography
                          className={classes.timelineDescription}
                        >
                          {event.description}
                        </Typography>
                      )}

                      {(stageNames.previous || stageNames.next) && (
                        <div className={classes.timelineStageChange}>
                          {stageNames.previous && (
                            <span className={classes.timelineStageChip}>
                              {stageNames.previous}
                            </span>
                          )}

                          {stageNames.previous && stageNames.next && (
                            <span className={classes.timelineArrow}>
                              →
                            </span>
                          )}

                          {stageNames.next && (
                            <span className={classes.timelineStageChip}>
                              {stageNames.next}
                            </span>
                          )}
                        </div>
                      )}

                      {eventMessage && (
                        <div className={classes.timelineMessageBox}>
                          <div className={classes.timelineMessageLabel}>
                            Mensagem analisada
                          </div>

                          <div className={classes.timelineMessageText}>
                            “{eventMessage}”
                          </div>
                        </div>
                      )}

                      <Typography className={classes.timelineDate}>
                        {formatDateTime(event.createdAt)}
                        {event.user?.name
                          ? ` • ${event.user.name}`
                          : ""}
                      </Typography>
                    </div>
                  </div>
                );
              })
            ) : (
              <Typography
                style={{
                  fontSize: ".76rem",
                  color: "#94A3B8",
                  fontStyle: "italic"
                }}
              >
                Nenhum evento registrado nesta oportunidade.
              </Typography>
            )}
          </div>
        )}

        <div className={classes.section}>
          <Typography className={classes.sectionTitle}>
            Negociação
          </Typography>

          <TextField
            fullWidth
            label="Produto / Serviço"
            variant="outlined"
            size="small"
            value={form.product}
            onChange={event =>
              setForm({
                ...form,
                product: event.target.value
              })
            }
            className={classes.field}
          />

          <TextField
            fullWidth
            label="Valor"
            variant="outlined"
            size="small"
            type="number"
            value={form.value}
            onChange={event =>
              setForm({
                ...form,
                value: event.target.value
              })
            }
            className={classes.field}
            InputProps={{
              startAdornment: (
                <AttachMoney
                  style={{
                    fontSize: 17,
                    color: "#0066FF",
                    marginRight: 4
                  }}
                />
              )
            }}
          />

          <TextField
            fullWidth
            label="Previsão de fechamento"
            variant="outlined"
            size="small"
            type="date"
            value={form.expectedCloseDate}
            onChange={event =>
              setForm({
                ...form,
                expectedCloseDate: event.target.value
              })
            }
            className={classes.field}
            InputLabelProps={{ shrink: true }}
          />
        </div>

        <div className={classes.section}>
          <Typography className={classes.sectionTitle}>
            Status
          </Typography>

          <div className={classes.statusGrid}>
            {statusOptions.map(option => (
              <div
                key={option.value}
                className={classes.statusBtn}
                onClick={() =>
                  setForm({
                    ...form,
                    status: option.value
                  })
                }
                style={{
                  background:
                    form.status === option.value
                      ? option.bg
                      : "transparent",
                  border: `1px solid ${
                    form.status === option.value
                      ? option.border
                      : "#E2E8F0"
                  }`,
                  color:
                    form.status === option.value
                      ? option.color
                      : "#94A3B8"
                }}
              >
                {option.icon}
                {option.label}
              </div>
            ))}
          </div>
        </div>

        <div className={classes.section}>
          <div className={classes.probabilityLabel}>
            <Typography className={classes.sectionTitle}>
              Probabilidade
            </Typography>

            <Typography
              style={{
                fontWeight: 900,
                color: "#0066FF"
              }}
            >
              {form.probability}%
            </Typography>
          </div>

          <Slider
            value={form.probability}
            onChange={(event, value) =>
              setForm({
                ...form,
                probability: value
              })
            }
            min={0}
            max={100}
            step={5}
            style={{ color: "#0066FF" }}
          />
        </div>

        <div
          className={classes.section}
          style={{ marginBottom: 0 }}
        >
          <Typography className={classes.sectionTitle}>
            Observações e próximos passos
          </Typography>

          <TextField
            fullWidth
            multiline
            rows={4}
            variant="outlined"
            size="small"
            placeholder="Registre informações importantes, objeções, próximos passos e follow-ups..."
            value={form.notes}
            onChange={event =>
              setForm({
                ...form,
                notes: event.target.value
              })
            }
          />
        </div>
      </DialogContent>

      <DialogActions className={classes.actions}>
        <Button
          onClick={onClose}
          className={classes.cancelBtn}
          variant="outlined"
        >
          Cancelar
        </Button>

        <Button
          onClick={handleSave}
          disabled={loading}
          className={classes.saveBtn}
          variant="contained"
        >
          {loading
            ? "Salvando..."
            : opportunity
            ? "Salvar alterações"
            : "Criar oportunidade"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OpportunityModal;
